import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";

import { useApp } from "contexts/AppContext";
import { BrlFormatCurrency, FormatPoints } from "../../../lib";

const Wrap = styled.div`
  margin-top: 24px;
`;

const SectionLabel = styled.h3`
  font-size: 18px;
  color: var(--text-dark, #1D1D1F);
  margin: 0 0 12px;
`;

const Cols = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 16px;
  text-align: center;
  align-items: end;
`;

const Col = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ColTop = styled.span`
  font-size: 16px;
  font-weight: 500;
  color: var(--text-dark, #1D1D1F);
`;

const ColValuePoints = styled.span`
  font-size: 18px;
  font-weight: 700;
  color: var(--text-dark, #1D1D1F);
`;

const ColValueReais = styled.span`
  font-size: 18px;
  font-weight: 700;
  color: #B68A3E;
`;

const Plus = styled.span`
  font-size: 20px;
  color: var(--text-dark, #1D1D1F);
  padding-bottom: 4px;
`;

const TRACK_HEIGHT = 4;
const THUMB_SIZE = 30;

function thumbSvgUrl(color) {
  const c = encodeURIComponent(color);
  const svg = encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="${THUMB_SIZE}" height="${THUMB_SIZE}" viewBox="0 0 30 30"><circle cx="15" cy="15" r="14" fill="#FFFFFF" stroke="${color}" stroke-width="1"/><path d="M12.5 11 L9 15 L12.5 19" fill="none" stroke="${color}" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M17.5 11 L21 15 L17.5 19" fill="none" stroke="${color}" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
  );
  // re-encode `#` inside color since encodeURIComponent leaves it alone in strings already encoded
  void c;
  return `url("data:image/svg+xml,${svg}")`;
}

const Range = styled.input.attrs({ type: "range" })`
  --filled: ${(p) => `${Math.min(100, Math.max(0, (p.$ratio || 0) * 100))}%`};
  --track-dark: ${(p) => p.$primaryColor || "#1D1D1F"};
  width: 100%;
  height: ${THUMB_SIZE}px;
  margin: 12px 0 8px;
  background: transparent;
  appearance: none;
  -webkit-appearance: none;
  outline: none;

  &::-webkit-slider-runnable-track {
    height: ${TRACK_HEIGHT}px;
    border-radius: ${TRACK_HEIGHT / 2}px;
    background: linear-gradient(
      to right,
      var(--track-dark) 0%,
      var(--track-dark) var(--filled),
      #B68A3E var(--filled),
      #B68A3E 100%
    );
  }
  &::-moz-range-track {
    height: ${TRACK_HEIGHT}px;
    border-radius: ${TRACK_HEIGHT / 2}px;
    background: linear-gradient(
      to right,
      var(--track-dark) 0%,
      var(--track-dark) var(--filled),
      #B68A3E var(--filled),
      #B68A3E 100%
    );
  }

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: ${THUMB_SIZE}px;
    height: ${THUMB_SIZE}px;
    border: 0;
    background: ${(p) => p.$thumb} center/contain no-repeat;
    margin-top: ${-(THUMB_SIZE - TRACK_HEIGHT) / 2}px;
    cursor: grab;
  }
  &::-moz-range-thumb {
    width: ${THUMB_SIZE}px;
    height: ${THUMB_SIZE}px;
    border: 0;
    background: ${(p) => p.$thumb} center/contain no-repeat;
    cursor: grab;
  }
  &:active::-webkit-slider-thumb,
  &:active::-moz-range-thumb {
    cursor: grabbing;
  }
`;

const Hint = styled.p`
  font-size: 12px;
  color: var(--text-dark, #1D1D1F);
  line-height: 1.5;
  margin: 0;
`;

export default function PointsSlider({
  pontos,
  reais,
  min = 0,
  max,
  onChange,
  onTrackChange,
}) {
  const { whitelabel } = useApp();
  const primaryColor =
    whitelabel?.theme?.button_primary_color ||
    whitelabel?.theme?.text_primary_color ||
    "#1D1D1F";

  return (
    <Wrap>
      <SectionLabel>Pontos</SectionLabel>
      <Cols>
        <Col>
          <ColTop>Pontos</ColTop>
          <ColValuePoints>{FormatPoints(pontos)}</ColValuePoints>
        </Col>
        <Plus>+</Plus>
        <Col>
          <ColTop>Reais</ColTop>
          <ColValueReais>{BrlFormatCurrency(reais)}</ColValueReais>
        </Col>
      </Cols>
      <Range
        min={min}
        max={max}
        step={1}
        value={pontos}
        $ratio={
          max > min ? (pontos - min) / (max - min) : max > 0 ? pontos / max : 0
        }
        $primaryColor={primaryColor}
        $thumb={thumbSvgUrl(primaryColor)}
        onChange={(e) => onChange(Number(e.target.value))}
        onMouseUp={(e) => onTrackChange?.(Number(e.target.value), reais)}
        onTouchEnd={(e) => onTrackChange?.(Number(e.target.value), reais)}
        onKeyUp={(e) => onTrackChange?.(Number(e.target.value), reais)}
      />
      <Hint>
        Use o círculo para ajustar a combinação de pontos e reais conforme sua
        preferência.
      </Hint>
    </Wrap>
  );
}

PointsSlider.propTypes = {
  pontos: PropTypes.number.isRequired,
  reais: PropTypes.number.isRequired,
  min: PropTypes.number,
  max: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  onTrackChange: PropTypes.func,
};
